import { useApolloClient, useMutation, useQuery } from '@apollo/client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, FloraButton, Flex, FloraInput, Spinner, FloraTypography } from '@grupoboticario/flora-react'

import UserForm from '@/components/UserForm/UserForm'
import { useHeaderContext } from '@/context/header'
import { useUserContext } from '@/context/user'
import CREATE_CONTACT from '@/graphql/mutation/createContact'
import PATCH_MEMBER from '@/graphql/mutation/createMember'
import { GET_CUSTOMER_ID_FOR_MEMBER_CREATION } from '@/graphql/queries/customerById'
import { GET_MEMBER_BY_DOCUMENT_NUMBER } from '@/graphql/queries/memberByDocumentNumber'
import { businessUnit } from '@/utils/themes/theme'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'
import { FadeIn } from '@/utils/useFadeIn/useFadeIn'

import * as P from '../pages.styles'
import * as N from './new.styles'

const NewUserPage = () => {
  /**
   * EMPTY = Has not typed any CPF
   * NEW_MEMBER = A CPF from a person that does not have a customer neither an member profile
   * CUSTOMER_EXISTS = A CPF from a person that only have a customer profile
   * MEMBER_EXISTS = A CPF from a person that already have a member profile
   */
  const [formState, setFormState] = useState('EMPTY')
  const [member, setMember] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [documentNumber, setDocumentNumber] = useState('')
  const [contacts, setContacts] = useState([])
  const { user } = useUserContext()
  const { setMember: setMemberContext } = useHeaderContext()
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const { refetch: getMember } = useQuery(GET_MEMBER_BY_DOCUMENT_NUMBER, { skip: true })
  const { refetch: getCustomer } = useQuery(GET_CUSTOMER_ID_FOR_MEMBER_CREATION, { skip: true })
  const [createMemberCall] = useMutation(PATCH_MEMBER, {
    onError: () => {}
  })

  const createContacts = async (contacts) => {
    if (!contacts || contacts.length === 0) {
      return Promise.resolve()
    }

    try {
      const promises = contacts.map((contact) =>
        apolloClient.mutate({
          mutation: CREATE_CONTACT,
          variables: {
            documentNumber,
            contact: {
              origin: {
                originId: 'backoffice-fidelidade',
                channel: 'crm',
                identifier: user?.name
              },
              documentNumber,
              value: contact?.value,
              contactType: contact.contactType,
              contactMethod: contact.contactMethod
            }
          }
        })
      )

      await Promise.all(promises)
    } catch (_error) {
      void _error
    }
  }

  const createMember = async (member, contacts) => {
    const { error, errors } = await createMemberCall({
      variables: {
        member: {
          ...member,
          documentNumber,
          documentType: 'CPF',
          status: 'ACTIVE',
          tier: 0,
          statusChange: {
            text: '',
            responsible: user.email,
            reason: undefined
          },
          origin: {
            originId: businessUnit(globalThis?.location?.hostname),
            channel: 'backoffice-fidelidade',
            identifier: user.email
          }
        }
      }
    })

    await createContacts(contacts)

    triggerToastSuccess(
      error || errors,
      'Perfil de participante criado com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.',
      () => {
        setMemberContext({})
        navigate('/members?refresh=true')
      }
    )
  }

  const checkForParticipantProfile = async (text) => {
    const documentNumber = text.replaceAll(/\D/g, '')
    setDocumentNumber(documentNumber)
    if (documentNumber.length === 11) {
      setIsLoading(true)
      const { error: memberError, data: memberProfile } = await getMember({ documentNumber })

      if (memberProfile?.member?.createdAt) {
        setFormState('MEMBER_EXISTS')
      } else {
        const { error: customerError, data: customerProfile } = await getCustomer({
          documentNumber
        })
        if (customerProfile?.customer?.email) {
          setMember(customerProfile?.customer)
          setFormState('CUSTOMER_EXISTS')
        } else if (!memberError && !customerError) {
          setFormState('NEW_MEMBER')
          setMemberContext({ documentNumber })
        }
      }

      setIsLoading(false)
    } else {
      setFormState('EMPTY')
    }
  }

  return (
    <Box css={P.pageWrapperCss}>
      <Flex direction="column" gap="$8">
        {formState === 'EMPTY' && (
          <Box>
            <FloraTypography fontSize="bodyLargeStandard">
              Digite o CPF do cliente, para checarmos se ele já possui algum tipo de cadastro
            </FloraTypography>
          </Box>
        )}
        <Flex align="center" gap="$3" wrap="wrap">
          <Box css={N.cpfInputBoxCss}>
            <FloraInput
              disabled={isLoading}
              id="cpf"
              label="CPF"
              onChange={(e) => checkForParticipantProfile(e.target.value)}
              required
              size="medium"
            />
          </Box>
          <Box>
            {isLoading && (
              <FadeIn>
                <Box>
                  <Spinner size="xs" speed="800ms" thickness="2px" />
                </Box>
              </FadeIn>
            )}
            <Box>
              {formState === 'MEMBER_EXISTS' && (
                <FadeIn>
                  <Box>
                    <FloraTypography fontSize="bodyLargeStandard">
                      Participante já existe,{' '}
                      <FloraTypography
                        as="u"
                        fontSize="bodyLargeStandard"
                        css={N.linkTextCss}
                        onClick={() => navigate(`/members/${documentNumber}`)}
                      >
                        clique aqui para ir pro perfil
                      </FloraTypography>
                    </FloraTypography>
                  </Box>
                </FadeIn>
              )}
              {formState === 'CUSTOMER_EXISTS' && (
                <FadeIn>
                  <FloraTypography fontSize="bodyLargeStandard">
                    Cliente já existente, confirme pra criar um perfil de participante:
                  </FloraTypography>
                </FadeIn>
              )}
            </Box>
          </Box>
        </Flex>
        <Box>
          {formState === 'NEW_MEMBER' && (
            <FadeIn>
              <UserForm
                onValidMemberInformationChange={(member, phones, emails) => {
                  setMember(member)
                  setContacts(phones?.concat(emails))
                }}
              />
            </FadeIn>
          )}
        </Box>
        {(formState === 'NEW_MEMBER' || formState === 'CUSTOMER_EXISTS') && (
          <Flex gap="$5" justify="flex-end" wrap="wrap">
            <FloraButton
              css={P.fullWidthButtonCss}
              isUppercase
              onClick={() => navigate('/members')}
              hierarchy="secondary"
            >
              Cancelar
            </FloraButton>
            <FloraButton
              css={P.fullWidthButtonCss}
              disabled={!member.email}
              isFull
              isLoading={isLoading}
              isUppercase
              onClick={() => createMember(member, contacts)}
              type="submit"
            >
              Criar participante
            </FloraButton>
          </Flex>
        )}
      </Flex>
    </Box>
  )
}

export default NewUserPage

export { NewUserPage as NewMemberPage }
